import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSequenceAccess } from "../../../store/slices/misc/sequenceAccess";
import { useFormContext } from "react-hook-form";
import { Button, Col, Row } from "reactstrap";
import { SettingOutlined } from "@ant-design/icons";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import usersApis from "../../../api/master/users";
import sitesApis from "../../../api/master/sites";
import boqApis from "../../../api/boq/boq";
import BOQSettingsDrawer from "./BOQSettingsDrawer";

interface SelectOptions {
  label: string;
  value: string;
}
const ConsumptionTarget_GeneralDetails: React.FC = (props: any) => {
  const { data: sequenceAccess } = useSelector(
    (state: any) => state.sequenceAccess
  );
  const dispatch: any = useDispatch();
  const {
    handleSubmit,
    watch,
    setValue,
    control,
    getValues,
    reset,
    formState: { errors },
  } = useFormContext();

  const [sitesOptions, setSitesOptions] = useState<SelectOptions[]>([]);
  const siteIdWatch = watch("siteId");
  const workLevelSettingWatch = watch("workLevelSetting");
  const previousSiteIdRef = React.useRef<string | undefined>();

  const SEGMENT_TO_WORK_LEVEL: Record<string, string> = {
    Tower: "TOWER_BLOCK",
    "Tower/Block": "TOWER_BLOCK",
    "Tower / Block": "TOWER_BLOCK",
    "Tower/ Block": "TOWER_BLOCK",
    Block: "TOWER_BLOCK",
    Villa: "VILLA",
    Floor: "FLOOR",
    Flat: "FLAT",
  };

  const [isWorkLevelDisabled, setIsWorkLevelDisabled] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleSettingsSubmit = (updatedSettings: any) => {
    setValue("limitPostTargetConsumption", updatedSettings.limitPostTargetConsumption);
    setValue("limitPostTargetOrder", updatedSettings.limitPostTargetOrder);
    setValue("setAlerts", updatedSettings.setAlerts);
    setValue("notifyUsers", updatedSettings.notifyUsers);
    setValue("notifyUserNames", updatedSettings.notifyUserNames);
  };

  const fetchSiteHierarchy = async (siteId: any) => {
    try {
      const { data, status } = await sitesApis.fetchSegmentLevelBySiteId(
        siteId
      );
      if (status && data) {
        const workLevelValue = SEGMENT_TO_WORK_LEVEL[data] || null;
        setValue("workLevelSetting", workLevelValue);
        setIsWorkLevelDisabled(true);
      } else {
        // If no data is returned, show "Site" by clearing the field
        setValue("workLevelSetting", null);
        setIsWorkLevelDisabled(true);
      }
    } catch (error) {
      console.log("Error fetching site hierarchy:", error);
      setValue("workLevelSetting", null);
      setIsWorkLevelDisabled(true);
    }
  };

  useEffect(() => {
    const previousSiteId = previousSiteIdRef.current;
    const siteChanged =
      previousSiteId !== undefined && previousSiteId !== siteIdWatch;

    if (siteIdWatch) {
      fetchSiteHierarchy(siteIdWatch);

      // If site changed, clear hierarchy data from all BOQ lines
      if (siteChanged) {
        const boqLines = getValues("boqLines");
        if (boqLines && Array.isArray(boqLines) && boqLines.length > 0) {
          boqLines.forEach((_, index: number) => {
            setValue(`boqLines.${index}.siteStructureUnitId`, null);
            setValue(`boqLines.${index}.siteStructureUnitName`, null);
            setValue(`boqLines.${index}.floorNo`, null);
            setValue(`boqLines.${index}.siteStructureSubUnitId`, null);
            setValue(`boqLines.${index}.siteStructureSubUnitName`, null);
            setValue(`boqLines.${index}.locationId`, null);
            setValue(`boqLines.${index}.locationName`, null);
          });
        }
      }
    }

    previousSiteIdRef.current = siteIdWatch;
  }, [siteIdWatch, setValue, getValues]);

  useEffect(() => {
    dispatch(fetchSequenceAccess());
  }, [dispatch]);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const data = await usersApis.getCurrentUser();
      if (!data?.siteAccessibility?.length) return;

      const accessibleSites = data.siteAccessibility.map((item: any) => ({
        label: item.siteName,
        value: item.siteID.toString(),
      }));

      const sitesWithBoq = await fetchSitesWithExistingBoq(
        data.siteAccessibility.map((item: any) => item.siteID).join(",")
      );

      const filtered = accessibleSites.filter(
        (opt: SelectOptions) => !sitesWithBoq.has(opt.value)
      );
      setSitesOptions(filtered);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSitesWithExistingBoq = async (
    siteIds: string
  ): Promise<Set<string>> => {
    const taken = new Set<string>();
    try {
      const apiResponse: any = await boqApis.fetchConsumptionTarget({
        sortOrder: "DESC",
        pageSize: 1000,
        direction: "P",
        pageDiff: 0,
        paginationId: null,
        siteIds,
      });
      const list =
        apiResponse?.data?.list ??
        apiResponse?.response?.data?.list ??
        apiResponse?.response?.list ??
        [];
      list.forEach((item: any) => {
        const id = item?.siteId ?? item?.siteID;
        if (id !== undefined && id !== null) taken.add(id.toString());
      });
    } catch (error) {
      console.log("Error fetching existing BOQs:", error);
    }
    return taken;
  };

  const submitForm = async (data: any) => {
    const payload = {};

    console.log("Payload:", payload);

    console.log("data", data);
    props.next(payload);
  };

  return (
    <>
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        <Row>
          <div
            style={{ justifyContent: "end", display: "flex", marginTop: "-2%" }}
          >
            <Button color="neutral" onClick={() => setSettingsOpen(true)}>
              Settings &nbsp; <SettingOutlined />
            </Button>
          </div>
        </Row>
        <Row className="gy-4">
          <Col lg="4">
            <FormInput
              name="boqTitle"
              label={`BOQ Title`}
              required={true}
              placeholder="Enter BOQ Title"
            />
          </Col>
          {!sequenceAccess?.includes("BOQ") ? (
            <Col lg="4">
              <FormInput
                name="targetNumber"
                label={`BOQ ID`}
                required={true}
                placeholder="Enter BOQ ID"
              />
            </Col>
          ) : (
            <></>
          )}
          <Col lg="4">
            <FormSelect
              name="siteId"
              label={`Site`}
              options={sitesOptions}
              required={true}
              placeholder="-Select-"
              tooltipText="Select the site where the materials will be used."
            />
          </Col>
          <Col lg="4">
            <FormSelect
              name="workLevelSetting"
              label="Site Hierarchy"
              options={[
                { label: "Tower / Block", value: "TOWER_BLOCK" },
                { label: "Villa", value: "VILLA" },
                { label: "Floor", value: "FLOOR" },
                { label: "Flat", value: "FLAT" },
              ]}
              placeholder={
                siteIdWatch && isWorkLevelDisabled && !workLevelSettingWatch
                  ? "Site"
                  : "Select Site Hierarchy"
              }
              disabled={isWorkLevelDisabled}
            />
          </Col>
        </Row>
        <div className="actions clearfix">
          <ul>
            <li>
              <Button color="primary" type="submit">
                Next
              </Button>
            </li>
          </ul>
        </div>
      </form>
      <BOQSettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSubmit={handleSettingsSubmit}
        initialValues={{
          limitPostTargetConsumption: watch("limitPostTargetConsumption"),
          limitPostTargetOrder: watch("limitPostTargetOrder"),
          setAlerts: watch("setAlerts"),
          notifyUsers: watch("notifyUsers"),
        }}
        siteId={siteIdWatch}
      />
    </>
  );
};

export default ConsumptionTarget_GeneralDetails;
